import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import { jobProspectMatchesQueryKey } from '@/hooks/use-job-prospect-matches'

import { useOpportunityApplicants } from './use-opportunity-applicants'

export function useInviteToApply(opportunityId: string | undefined) {
  const queryClient = useQueryClient()
  const { board } = useOpportunityApplicants(opportunityId)
  const [invitedLocally, setInvitedLocally] = useState<Set<string>>(() => new Set())

  const appliedProfileIds = useMemo(() => {
    const ids = new Set<string>()
    for (const applicant of board?.applicants ?? []) {
      ids.add(applicant.profile.id)
    }
    return ids
  }, [board?.applicants])

  const invitedProfileIds = useMemo(() => {
    const ids = new Set(invitedLocally)
    for (const applicant of board?.applicants ?? []) {
      if (applicant.status === 'invited') {
        ids.add(applicant.profile.id)
      }
    }
    return ids
  }, [board?.applicants, invitedLocally])

  /** Profiles who applied (or progressed) — excludes invite-only `invited` status. */
  const selfAppliedProfileIds = useMemo(() => {
    const ids = new Set<string>()
    for (const applicant of board?.applicants ?? []) {
      if (applicant.status !== 'invited') {
        ids.add(applicant.profile.id)
      }
    }
    return ids
  }, [board?.applicants])

  const inviteMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!opportunityId) {
        throw new Error('Job id missing')
      }
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/invite`, {
        method: 'POST',
        body: JSON.stringify({
          profile_id: profileId,
          opportunity_id: opportunityId,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to invite candidate')
      }
      return profileId
    },
    onSuccess: (profileId) => {
      setInvitedLocally((prev) => new Set(prev).add(profileId))
      toast.success('Candidate invited successfully!')
      queryClient.invalidateQueries({ queryKey: ['opportunity-applicants', opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['search-sessions'] })
      if (opportunityId) {
        queryClient.invalidateQueries({ queryKey: jobProspectMatchesQueryKey(opportunityId) })
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to invite candidate')
    },
  })

  const inviteProfile = useCallback(
    (profileId: string) => {
      if (!opportunityId) return
      if (appliedProfileIds.has(profileId) || invitedProfileIds.has(profileId)) return
      if (inviteMutation.isPending) return
      inviteMutation.mutate(profileId)
    },
    [appliedProfileIds, invitedProfileIds, inviteMutation, opportunityId],
  )

  const isApplied = useCallback(
    (profileId: string) => appliedProfileIds.has(profileId) && !invitedProfileIds.has(profileId),
    [appliedProfileIds, invitedProfileIds],
  )

  const isInvited = useCallback(
    (profileId: string) => invitedProfileIds.has(profileId),
    [invitedProfileIds],
  )

  return {
    appliedProfileIds,
    selfAppliedProfileIds,
    inviteProfile,
    isApplied,
    isInvited,
    isInviting: inviteMutation.isPending,
    invitingProfileId: inviteMutation.variables,
  }
}
